// Centralized runtime config. Base URLs come from build-time env with
// sensible localhost defaults so the app works out of the box.
export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3001';

export const WS_URL =
  (import.meta.env.VITE_WS_URL as string | undefined) ?? 'ws://localhost:3001';

// Unset Docker ARGs bake as empty strings (not undefined), which would defeat a
// plain `??` fallback — so coalesce empty/whitespace to the default too.
const envOr = (value: string | undefined, fallback: string): string => {
  const v = (value ?? '').trim();
  return v.length > 0 ? v : fallback;
};

/** Canonical marketing site (the "Powered by Cartiq" mark links here). */
export const SITE_URL = envOr(import.meta.env.VITE_SITE_URL, 'https://cartiq.shop');

/**
 * Resolve the active store slug from the current URL.
 * Supports `/?store=slug` query param and `/:slug` path segment.
 */
export function resolveStoreSlug(): string {
  if (typeof window === 'undefined') return 'demo';
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('store');
  if (fromQuery) return fromQuery;

  const seg = window.location.pathname.split('/').filter(Boolean)[0];
  if (seg) return seg;

  return 'demo';
}

export type StoreRoute =
  | { view: 'chat'; slug: string }
  | { view: 'checkout'; slug: string; token: string }
  | { view: 'success'; slug: string }
  | { view: 'cancel'; slug: string };

/**
 * Resolve which storefront surface to render from the URL:
 * - `/:slug/checkout?c=<token>` — browser-checkout handoff: a cart minted
 *   elsewhere (e.g. a WhatsApp conversation) that the shopper pays for here.
 * - `/:slug/success` / `/:slug/cancel` — where the hosted payment page returns
 *   the shopper after they pay or back out.
 * - everything else — the chat surface.
 */
export function resolveRoute(): StoreRoute {
  const slug = resolveStoreSlug();
  if (typeof window === 'undefined') return { view: 'chat', slug };
  const segments = window.location.pathname.split('/').filter(Boolean);
  const token = new URLSearchParams(window.location.search).get('c');
  if (segments.includes('checkout') && token) {
    return { view: 'checkout', slug, token };
  }
  if (segments.includes('success')) return { view: 'success', slug };
  if (segments.includes('cancel')) return { view: 'cancel', slug };
  return { view: 'chat', slug };
}
